// src/components/TeamCard.tsx

import React from 'react';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Check, ArrowRight } from 'lucide-react';

import { TeamData, TeamAccentColor } from '@dopomogai/types';
import { renderIcon } from '@dopomogai/utils';
import {Button} from '@dopomogai/ui';

interface TeamCardProps {
  team: TeamData;
  onAction: (actionType: string, payload: any) => void;
  className?: string;
}

const accentColorMap: Record<TeamAccentColor, { bg: string; text: string; buttonBg: string; buttonHoverBg: string }> = {
  orange: { bg: 'bg-orange-100', text: 'text-orange-700', buttonBg: 'bg-orange-500', buttonHoverBg: 'hover:bg-orange-400' },
  red:    { bg: 'bg-red-100', text: 'text-red-700', buttonBg: 'bg-red-500', buttonHoverBg: 'hover:bg-red-400' },
  blue:   { bg: 'bg-blue-100', text: 'text-blue-700', buttonBg: 'bg-blue-500', buttonHoverBg: 'hover:bg-blue-400' },
  purple: { bg: 'bg-purple-100', text: 'text-purple-700', buttonBg: 'bg-purple-500', buttonHoverBg: 'hover:bg-purple-400' },
  green:  { bg: 'bg-emerald-100', text: 'text-emerald-700', buttonBg: 'bg-emerald-500', buttonHoverBg: 'hover:bg-emerald-400' },
  slate:  { bg: 'bg-slate-200', text: 'text-slate-700', buttonBg: 'bg-slate-600', buttonHoverBg: 'hover:bg-slate-500' },
};

export const TeamCard: React.FC<TeamCardProps> = ({ team, onAction, className }) => {
  // --- I18N IMPLEMENTATION ---
  const { t } = useTranslation('teams_showcase');
  const colorTheme = accentColorMap[team.accentColor] || accentColorMap.slate;

  const handleCardClick = () => {
    onAction('NAVIGATE_TO_TEAM_PAGE', { slug: team.slug });
  };
  
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleCardClick();
  };

  const translatedName = t(team.nameKey);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className={clsx('relative group w-full h-full', className)}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleCardClick()}
      aria-label={`${t('card.exploreButton')} for the ${translatedName}`}
    >
      <div className="absolute inset-0 bg-black rounded-xl" />
      <div
        className={clsx(
          'relative bg-white rounded-xl border-2 border-black overflow-hidden flex flex-col h-full',
          'transition-transform duration-200 ease-out transform',
          'group-hover:-translate-x-1.5 group-hover:-translate-y-1.5',
          'group-active:translate-x-0 group-active:translate-y-0'
        )}
      >
        <div className={clsx('p-6 border-b-2 border-black', colorTheme.bg)}>
          <div className="flex items-start gap-4">
            <div className={clsx('p-3 rounded-lg border-2 border-black shrink-0', colorTheme.bg)}>
              {renderIcon(team.icon, { size: 28, className: colorTheme.text })}
            </div>
            <div>
              {/* --- TRANSLATED --- */}
              <h3 className="text-xl font-bold text-gray-900 leading-tight">{translatedName}</h3>
            </div>
          </div>
        </div>
        <div className="px-6 py-5 flex-grow">
          {/* --- TRANSLATED --- */}
          <p className="text-slate-600 mb-5">{t(team.taglineKey)}</p>
          <ul className="space-y-2.5">
            {/* --- TRANSLATED LIST --- */}
            {team.focusAreasKeys.map((areaKey, index) => (
              <li key={index} className="flex items-start gap-2.5 text-sm">
                <Check size={16} className="text-emerald-500 mt-0.5 shrink-0" />
                <span className="text-slate-700">{t(areaKey)}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="px-6 pt-4 pb-6 mt-auto">
          <Button
            // --- TRANSLATED ---
            textKey="teams_showcase:card.exploreButton"
            icon={<ArrowRight size={16} />}
            iconPosition="right"
            onClick={handleButtonClick}
            fullWidth
            ariaLabel={`Explore the ${translatedName}`}
            className={clsx(
              "text-white border-black",
              colorTheme.buttonBg,
              colorTheme.buttonHoverBg,
            )}
          />
        </div>
      </div>
    </motion.div>
  );
};